import React from 'react';
import firebase from '../../utilities/firebase';

import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, useTheme } from '@mui/material/styles';

import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import NoteAddIcon from '@mui/icons-material/NoteAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AssignmentIcon from '@mui/icons-material/Assignment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';

import RequestManager from '../../utilities/requestManager';
import { StackedDialog, useSelectMenuProps, useDatePickerSlotProps } from '../../utilities/ModalStackContext';
import { StateManager } from '../../utilities/stateManager';

const BLUE = '#023e84';

// Which rules produced a saved title read. Must match READ_RULES in the backend
// (skyway-functions dmv/titleRead.js). A read saved under older rules still
// carries the proposals those rules made — the plate off the paper title among
// them — so it is shown as no read at all and the clerk reads the title again.
const READ_RULES = 2;
const currentRead = (title) => {
  const read = title && title.ai_read;
  return read && read.rules === READ_RULES ? read : null;
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

// A value that goes on the printed form, and where it comes from.
function Field({ label, value, missing, required }) {
  const theme = useTheme();
  const blank = !String(value ?? '').trim();
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: blank ? 400 : 500,
          color: blank ? (required ? theme.palette.error.main : 'text.disabled') : 'text.primary',
        }}
      >
        {blank ? (required ? 'Missing' : '—') : value}
      </Typography>
    </Box>
  );
}

// How sure the read is. Anything below "high" is worth a second look before it
// goes on a legal form, so the chip says so plainly rather than in a colour.
const CONFIDENCE = {
  high: { label: 'clear', color: 'success' },
  medium: { label: 'check it', color: 'warning' },
  low: { label: 'unsure', color: 'error' },
};

// One value the title reader wants to put on the record.
function ProposalRow({ proposal, applied, busy, onApply }) {
  const theme = useTheme();
  const c = CONFIDENCE[proposal.confidence] || CONFIDENCE.low;
  const shown = proposal.display !== undefined && proposal.display !== null
    ? String(proposal.display)
    : Array.isArray(proposal.value) ? proposal.value.join(', ') : String(proposal.value);
  const current = Array.isArray(proposal.current) ? proposal.current.join(', ') : String(proposal.current || '');

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75,
      borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' },
    }}>
      <Box sx={{ width: 150, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{proposal.label}</Typography>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{shown}</Typography>
        {current && (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            now: {current}
          </Typography>
        )}
        {proposal.help && (
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
            {proposal.help}
          </Typography>
        )}
      </Box>

      <Chip size="small" label={`${c.label} · ${proposal.source}`}
        sx={{
          height: 20, fontSize: '0.68rem',
          bgcolor: alpha(theme.palette[c.color].main, 0.12),
          color: theme.palette[c.color].dark,
        }} />

      {applied ? (
        <Chip size="small" icon={<CheckIcon sx={{ fontSize: 13 }} />} label="Applied"
          sx={{ height: 22, fontSize: '0.68rem', bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.dark' }} />
      ) : (
        <Button size="small" disabled={busy} onClick={() => onApply(proposal)}
          sx={{ fontSize: '0.72rem', textTransform: 'none', minWidth: 58 }}>
          Apply
        </Button>
      )}
    </Box>
  );
}

function SectionHead({ icon, title, hint, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{ color: BLUE, display: 'flex' }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: BLUE, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
          {title}
        </Typography>
        {hint && <Typography variant="caption" sx={{ color: 'text.disabled' }}>{hint}</Typography>}
      </Box>
      {action}
    </Box>
  );
}

// Every date on the title application.
//
// These were a native `<input type="date">` for the deal date and plain
// MM/DD/YYYY text boxes for the rest. The text boxes were uncontrolled — a
// `defaultValue` plus a `key` to force a remount whenever something rewrote the
// value underneath — which is fragile and gave no calendar at all.
//
// This is the picker the rest of the app uses (MUI X with the moment adapter,
// same as Sales.js), so it looks and behaves like every other date field here.
// It is controlled, which retires the remount trick.
//
// The form stores dates as STRINGS, and in two shapes: the deal date is
// YYYY-MM-DD, the printed boxes are MM/DD/YYYY. So parse permissively and write
// back in whichever shape the field wants.
const DATE_IN = ['MM/DD/YYYY', 'YYYY-MM-DD', 'M/D/YYYY', 'MM/DD/YY', moment.ISO_8601];

function TitleDate({
  label, value, onChange, format = 'MM/DD/YYYY',
  helperText, minDate, error, disabled,
}) {
  const slots = useDatePickerSlotProps();
  const parsed = value ? moment(value, DATE_IN, false) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DatePicker
        label={label}
        value={parsed && parsed.isValid() ? parsed : null}
        onChange={(next) => onChange(next && next.isValid() ? next.format(format) : '')}
        minDate={minDate}
        disabled={disabled}
        slotProps={{
          field: { clearable: true },
          textField: { size: 'small', fullWidth: true, helperText, error },
          popper: slots.popper,
          dialog: slots.dialog,
        }}
      />
    </LocalizationProvider>
  );
}

// The title application, and the data that fills it.
//
// This replaces the old Title tab, which was a wall of inputs with no indication
// of what any of it was for. Buyer details are shown as they will print, and
// edited on the customer record where they belong. Only the fields with no other
// home in the app — the vehicle details the form asks for and the application
// options — are edited here.
export default function TitleAppDialog({ open, onClose, car = {}, caseType, onGenerated }) {
  const theme = useTheme();
  const menuProps = useSelectMenuProps();
  const [busy, setBusy] = React.useState(null);
  const stockNumber = car.stock;

  const isFlorida = caseType === 'in_state';
  const primaryDoctype = isFlorida ? 'title' : 'title-skyway';

  const buyer = car.buyer || {};
  const title = car.title || {};

  // Whose name goes on the application. A Florida buyer is titled in their own
  // name; anyone else is titled into the Skyway Classics name.
  const buyerName = [buyer.first_name, buyer.last_name].filter(Boolean).join(' ').trim()
    || buyer.display_name || buyer.company_name || '';
  const titledTo = isFlorida ? (buyerName || '(no buyer on the deal)') : 'Skyway Classics';

  const carUpdate = (id, value) => {
    StateManager.updateCar({ [id]: value });
    firebase.firestore().doc('cars/' + stockNumber).set({ [id]: value }, { merge: true })
      .catch(e => StateManager.setAlertAndOpen('Could not save: ' + e.message, 'error'));
  };

  // Several keys at once. Calling `titleUpdate` twice in a row writes both to
  // Firestore but leaves local state holding only the second, because each call
  // builds its patch from the same `title` it closed over.
  const titlePatch = (patch) => {
    StateManager.updateCar({ title: { ...title, ...patch } });
    firebase.firestore().doc('titles/' + stockNumber).set(patch, { merge: true })
      .catch(e => StateManager.setAlertAndOpen('Could not save: ' + e.message, 'error'));
  };

  const titleUpdate = (id, value) => titlePatch({ [id]: value });

  // ── Reading the paper title ───────────────────────────────────────────────
  // Body type, weight and previous state are set on fewer than one car in
  // fifteen, so those boxes print empty and someone fills them in by hand off
  // the title. All of them are printed on the title we already photographed, so
  // read it instead. Nothing is written until a person applies it — the read
  // only proposes.
  //
  // The plate and an out-of-state title number are read too, but they go to
  // titles/{stock} and never onto the car: the plate on the paper title is the
  // previous owner's, and Section 2 wants a FLORIDA title number.
  const [read, setRead] = React.useState(currentRead(title));
  const [reading, setReading] = React.useState(false);
  const [applied, setApplied] = React.useState({});

  React.useEffect(() => { setRead(currentRead(title)); }, [title.ai_read]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasTitleImages = !!car.title_front;

  const runRead = async (force) => {
    setReading(true);
    const res = await RequestManager.post({
      function: 'readTitleForApp',
      variables: { stockNumber, force: !!force },
    });
    setReading(false);
    if (!res || res.success === false) {
      return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not read the title.', 'error');
    }
    setApplied({});
    setRead(res);
    const count = (res.proposals || []).filter(p => !p.unchanged).length;
    StateManager.setAlertAndOpen(
      count ? `Read the title — ${count} value${count === 1 ? '' : 's'} to apply.` : 'Read the title — nothing new to apply.',
      'success',
    );
  };

  // Apply straight away and write in the background. A wrong value is undone by
  // editing the field, so there is no reason to make the user wait on Firestore.
  const applyProposal = (p) => {
    setApplied(prev => ({ ...prev, [p.key]: true }));

    // The odometer disclosure is three flags on the car, not one value.
    const patch = p.key === 'odometer_brand' ? p.value : { [p.key]: p.value };

    if (p.target === 'title') {
      titlePatch(patch);
    } else {
      StateManager.updateCar(patch);
      firebase.firestore().doc('cars/' + stockNumber).set(patch, { merge: true })
        .catch(e => {
          setApplied(prev => ({ ...prev, [p.key]: false }));
          StateManager.setAlertAndOpen('Could not save: ' + e.message, 'error');
        });
    }
  };

  const proposals = ((read && read.proposals) || []).filter(p => !p.unchanged);
  const flags = (read && read.flags) || [];

  // ── Who holds the lien ────────────────────────────────────────────────────
  // Section 4 reads one field, `titles/{stock}.lien_holder`, and for a long time
  // nothing could write it — this box was disabled and pointed at a finance tab
  // field that does not exist, so a financed deal printed the lienholder blank.
  // The backend now reads the lender off the deal (the finance tab's ticked
  // bank, or the funding deposit's memo) and reports where it got it. Asking on
  // open costs one Firestore read and no model call, and what it reports is what
  // the application will print.
  const [lien, setLien] = React.useState(null);
  const lienName = (title.lien_holder && title.lien_holder.name) || '';

  React.useEffect(() => {
    if (!open || !stockNumber) return undefined;
    let live = true;
    RequestManager.post({
      function: 'resolveTitleLien',
      variables: { stockNumber, doctype: primaryDoctype },
    }).then(res => { if (live && res && res.success !== false) setLien(res); })
      // The dialog still works off the saved lienholder if this cannot be
      // reached, so a failure here is quiet rather than an alert over the form.
      .catch(() => {});
    return () => { live = false; };
  }, [open, stockNumber, primaryDoctype, lienName, title.no_lien]);

  const lenders = StateManager.banks || [];
  // What the application will print: the lienholder somebody saved, or failing
  // that the one the backend read off the deal.
  const effectiveLien = title.lien_holder || (lien && lien.lien) || null;

  // Records saved before lenders had ids carry only a name, so fall back to
  // matching on it.
  const lenderIdFor = (holder) => {
    if (!holder) return '';
    const byId = holder.id && lenders.find(b => b.data && b.data.id === holder.id);
    if (byId) return byId.data.id;
    const byName = holder.name && lenders.find(b => b.data && b.data.name === holder.name);
    return byName ? byName.data.id : '';
  };

  // `StateManager.banks` is the list we still send credit applications to, so a
  // lender that has gone inactive is missing from it — and a deal it funded
  // years ago still has to print. Keep whichever lender the deal points at in
  // the list, active or not.
  const lienOptions = (() => {
    const options = lenders.map(b => ({ id: b.data.id, label: b.label, data: b.data }));
    if (effectiveLien && !options.some(o => o.id === lenderIdFor(effectiveLien))) {
      const id = effectiveLien.id || effectiveLien.name;
      if (id) options.push({ id, label: effectiveLien.name || id, data: effectiveLien });
    }
    return options;
  })();

  const lienValue = title.no_lien === true
    ? 'none'
    : (lenderIdFor(effectiveLien) || (effectiveLien ? (effectiveLien.id || effectiveLien.name || '') : ''));

  const setLienHolder = (value) => {
    if (value === 'none') return titlePatch({ no_lien: true, lien_holder: null });
    if (value === '') return titlePatch({ no_lien: false, lien_holder: null });
    const chosen = lienOptions.find(o => o.id === value);
    if (!chosen) return undefined;
    return titlePatch({ no_lien: false, lien_holder: { ...chosen.data } });
  };

  // The slot a generated application is filed in follows the DOCTYPE, not the
  // case. Keying it off `isFlorida` meant "Make in Skyway name instead" on a
  // Florida deal filed the Skyway-name PDF under `title_app` — and the server
  // replaces a packet entry by requirement key, so it quietly overwrote the
  // customer-name application while the row still read "Title Application".
  const requirementKeyFor = (doctype) =>
    (doctype === 'title-skyway' ? 'title_app_skyway' : 'title_app');

  const generate = async (doctype) => {
    setBusy(doctype);
    const res = await RequestManager.post({
      function: 'generateDmvDocument',
      variables: { stockNumber, doctype, requirementKey: requirementKeyFor(doctype) },
    });
    setBusy(null);
    if (!res || res.success === false) {
      return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not make the title application.', 'error');
    }
    StateManager.setAlertAndOpen(`${res.label} added to the packet, titled to ${res.titledTo}.`, 'success');
    onGenerated?.();
  };

  // Preview through the same builder that files the document. It used to call
  // the legacy `preparePDF`, which is a second copy of the fill logic — so the
  // preview could show boxes the filed application did not have.
  const preview = async (doctype) => {
    setBusy('preview');
    try {
      const res = await RequestManager.post({
        function: 'generateDmvDocument',
        variables: { stockNumber, doctype, preview: true },
      });
      if (res && res.url) window.open(res.url, '_blank');
      else StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not build a preview.', 'error');
    } finally { setBusy(null); }
  };

  const ready = car.deal_loaded && car.buyer_loaded && car.title_loaded;

  // What the form needs that we do not have. Only flagged for the in-state case,
  // because the Skyway version fills the owner details from the house account.
  const gaps = [];
  if (isFlorida) {
    if (!buyerName) gaps.push('Buyer name');
    if (!buyer.address1) gaps.push('Buyer address');
    if (!buyer.dl) gaps.push('Buyer driver licence number');
    if (!buyer.birthday) gaps.push('Buyer date of birth');
  }
  if (isFlorida && !title.owner_county) gaps.push("Owner's county");
  if (!car.body_type) gaps.push('Body type');
  if (!car.prev_state) gaps.push('Previous title state');
  // Section 2 asks for a FLORIDA title number. A car whose last title came from
  // another state has none, and asking for one is what got an out-of-state
  // number typed into that box.
  if (car.prev_state === 'FL' && !car.title_number) gaps.push('Florida title number');
  if (!car.weight) gaps.push('Weight');
  if (!title.app_type) gaps.push('Application type');
  // A financed deal whose lender we cannot name prints Section 4 blank, which
  // is the right thing to print and the wrong thing to file.
  if (lien && lien.unresolved) gaps.push('Lien holder');

  const buyerAddress = [buyer.address1, buyer.city, buyer.state, buyer.zip].filter(Boolean).join(', ');

  return (
    <StackedDialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Title Application</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              HSMV 82040 · {car.stock}{car.year ? ` · ${[car.year, car.make, car.model].filter(Boolean).join(' ')}` : ''}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            label={`Titled to ${titledTo}`}
            sx={{
              fontWeight: 700, bgcolor: alpha(BLUE, 0.1), color: BLUE,
              border: '1px solid', borderColor: alpha(BLUE, 0.3), maxWidth: 320,
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: alpha(theme.palette.grey[100], 0.4) }}>
        {!ready ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading the deal…</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {gaps.length > 0 && (
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.08),
                                         border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.35) }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorOutlineIcon sx={{ fontSize: 18, color: 'warning.dark' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                    The form will print with blanks: {gaps.join(', ')}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* What the paper title says. */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <SectionHead
                icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
                title="Read from the paper title"
                hint={hasTitleImages
                  ? (read
                    ? `Read ${read.had_back ? 'the front and back' : 'the front only — no photo of the back'}.`
                    : 'Reads the front and back and proposes the values the form asks for.')
                  : 'Upload a photo of the title on the DMV tab first.'}
                action={hasTitleImages ? (
                  <Button
                    size="small"
                    disabled={reading || !!busy}
                    startIcon={reading
                      ? <CircularProgress size={13} color="inherit" />
                      : read ? <RefreshIcon sx={{ fontSize: 15 }} /> : <AutoAwesomeIcon sx={{ fontSize: 15 }} />}
                    onClick={() => runRead(!!read)}
                    sx={{ fontSize: '0.72rem', textTransform: 'none', color: BLUE }}
                  >
                    {reading ? 'Reading…' : read ? 'Read again' : 'Read the title'}
                  </Button>
                ) : null}
              />

              {flags.length > 0 && (
                <Stack spacing={0.5} sx={{ mb: proposals.length ? 1.5 : 0 }}>
                  {flags.map((f, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      {f.level === 'note'
                        ? <DescriptionIcon sx={{ fontSize: 15, color: 'text.disabled', mt: '2px' }} />
                        : <ErrorOutlineIcon sx={{ fontSize: 15, mt: '2px', color: f.level === 'blocking' ? 'error.main' : 'warning.dark' }} />}
                      <Typography variant="caption" sx={{
                        color: f.level === 'blocking' ? 'error.main' : f.level === 'warning' ? 'warning.dark' : 'text.secondary',
                        fontWeight: f.level === 'blocking' ? 700 : 400,
                      }}>
                        {f.message}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}

              {read && proposals.length === 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Nothing on the title differs from what the record already says.
                </Typography>
              )}

              {proposals.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                    <Button size="small" disabled={!!busy}
                      onClick={() => proposals.forEach(p => !applied[p.key] && applyProposal(p))}
                      sx={{ fontSize: '0.72rem', textTransform: 'none', fontWeight: 700, color: BLUE }}>
                      Apply all {proposals.length}
                    </Button>
                  </Box>
                  {proposals.map(p => (
                    <ProposalRow key={p.key} proposal={p} applied={!!applied[p.key]}
                      busy={!!busy} onApply={applyProposal} />
                  ))}
                </>
              )}
            </Paper>

            {/* Who it is for — read only, edited on the record that owns it. */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <SectionHead
                icon={<PersonIcon sx={{ fontSize: 18 }} />}
                title={isFlorida ? 'Owner — the buyer' : 'Owner — Skyway Classics'}
                hint={isFlorida
                  ? 'Filled from the customer record. Edit it there and it changes everywhere.'
                  : 'The buyer is outside Florida, so the application is filed in the Skyway Classics name.'}
                action={isFlorida && buyer.id ? (
                  <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    onClick={() => StateManager.openCustomerModal(buyer.id)}
                    sx={{ fontSize: '0.72rem', textTransform: 'none', color: BLUE }}>
                    Open buyer record
                  </Button>
                ) : null}
              />
              {isFlorida ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}><Field label="Name" value={buyerName} required /></Grid>
                  <Grid item xs={12} sm={8}><Field label="Address" value={buyerAddress} required /></Grid>
                  <Grid item xs={6} sm={3}><Field label="Driver licence #" value={buyer.dl} required /></Grid>
                  <Grid item xs={6} sm={3}><Field label="Date of birth" value={buyer.birthday} required /></Grid>
                  <Grid item xs={6} sm={3}><Field label="Sex" value={buyer.sex} /></Grid>
                  <Grid item xs={6} sm={3}><Field label="Phone" value={buyer.phone_number} /></Grid>
                </Grid>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Owner details come from the Skyway Classics house account. Nothing to fill in.
                </Typography>
              )}
            </Paper>

            {/* Vehicle details the form asks for. These live on the car and have no
                other home in the app, so they are edited here. */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <SectionHead
                icon={<DirectionsCarIcon sx={{ fontSize: 18 }} />}
                title="Vehicle details for the form"
                hint="Read these off the paper title. Saved on the car."
              />
              <Grid container spacing={2}>
                {/* Each box is keyed on its saved value, so a value applied from
                    the title read replaces what the box shows. */}
                <Grid item xs={12} sm={4}>
                  <TextField label="Body type" size="small" fullWidth key={'body_type:' + (car.body_type || '')} defaultValue={car.body_type || ''}
                    onBlur={(e) => e.target.value !== (car.body_type || '') && carUpdate('body_type', e.target.value)}
                    error={!car.body_type} helperText={!car.body_type ? 'Required' : ' '} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Weight" size="small" fullWidth key={'weight:' + (car.weight || '')} defaultValue={car.weight || ''}
                    onBlur={(e) => e.target.value !== (car.weight || '') && carUpdate('weight', e.target.value)}
                    helperText=" " />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Florida title number" size="small" fullWidth key={'title_number:' + (car.title_number || '')} defaultValue={car.title_number || ''}
                    onBlur={(e) => e.target.value !== (car.title_number || '') && carUpdate('title_number', e.target.value)}
                    helperText={car.prev_state && car.prev_state !== 'FL' ? 'Out-of-state title — leave blank' : ' '} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  {/* The plate the buyer is moving onto this car. The plate on the
                      title we took in belongs to the previous owner and never
                      goes here, which is why the title read does not fill it. */}
                  <TextField label="Plate to transfer" size="small" fullWidth key={'license_plate:' + (car.license_plate || '')} defaultValue={car.license_plate || ''}
                    onBlur={(e) => e.target.value !== (car.license_plate || '') && carUpdate('license_plate', e.target.value)}
                    helperText="Only a plate the buyer is transferring on" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Previous title state</InputLabel>
                    <Select label="Previous title state" MenuProps={menuProps}
                      value={car.prev_state || ''} onChange={(e) => carUpdate('prev_state', e.target.value)}>
                      <MenuItem value="">—</MenuItem>
                      {US_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Fuel type</InputLabel>
                    <Select label="Fuel type" MenuProps={menuProps}
                      value={car.fuel_type || ''} onChange={(e) => carUpdate('fuel_type', e.target.value)}>
                      <MenuItem value="">—</MenuItem>
                      {(StateManager.fuels || []).map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>
                VIN, year, make, model, colour and mileage come from the car record.
              </Typography>
              {(title.prior_title_number || title.prior_plate) && (
                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                  On the paper title:{' '}
                  {[
                    title.prior_title_number && `title number ${title.prior_title_number}`,
                    title.prior_plate && `plate ${title.prior_plate}`,
                  ].filter(Boolean).join(' · ')}. Kept for the file — neither prints on this form.
                </Typography>
              )}
            </Paper>

            {/* Application options — the only data that lives on titles/{stock}. */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <SectionHead
                icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                title="Application options"
                hint="How the state should process it."
              />
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth error={!title.app_type}>
                    <InputLabel>Application type</InputLabel>
                    <Select label="Application type" MenuProps={menuProps}
                      value={title.app_type || ''} onChange={(e) => titleUpdate('app_type', e.target.value)}>
                      <MenuItem value="">—</MenuItem>
                      {(StateManager.app_types || []).map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl size="small" fullWidth error={!!(lien && lien.unresolved)}>
                    <InputLabel>Lien holder</InputLabel>
                    <Select label="Lien holder" MenuProps={menuProps}
                      value={lienValue}
                      onChange={(e) => setLienHolder(e.target.value)}>
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value="none">No lien</MenuItem>
                      {lienOptions.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
                    </Select>
                    {lien && (
                      <Typography variant="caption"
                        sx={{ mt: 0.5, color: lien.unresolved ? 'error.main' : 'text.secondary' }}>
                        {lien.note}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Tooltip title="The state prints the title the same day instead of mailing it">
                    <FormControlLabel
                      control={<Switch size="small" checked={!!title.fast_title}
                        onChange={(e) => titleUpdate('fast_title', e.target.checked)} />}
                      label={<Typography variant="body2">Fast title</Typography>}
                    />
                  </Tooltip>
                </Grid>

                {/* The sale, acquired and signing dates are worked out from the
                    records and corrected under "Packet dates" on the DMV packet,
                    so they are not edited here. */}
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    The application dates come from the signed paperwork, the title transfer and the
                    final deposit. Check or correct them under Packet dates on the DMV packet.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Owner's county" size="small" fullWidth
                    key={'owner_county:' + (title.owner_county || '')} defaultValue={title.owner_county || ''}
                    onBlur={(e) => e.target.value !== (title.owner_county || '') && titleUpdate('owner_county', e.target.value)}
                    helperText={isFlorida ? 'Required for a Florida owner' : ' '}
                    error={isFlorida && !title.owner_county} />
                </Grid>

                {/* The date the title last changed hands. Every date above has to
                    fall after it. Read off the title where it can be, but plenty
                    of titles carry no date at all, and then the clerk puts it in.
                    Nothing is blocked either way — it decides what gets flagged. */}
                <Grid item xs={12} sm={4}>
                  <TitleDate
                    label="Last transfer on the title"
                    value={title.last_transfer_date}
                    onChange={(v) => titleUpdate('last_transfer_date', v)}
                    helperText="Blank reads it off the title"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2.5, py: 2 }}>
        <Button size="small" startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
          disabled={!ready || !!busy} onClick={() => preview(primaryDoctype)}>
          {busy === 'preview' ? 'Opening…' : 'Preview'}
        </Button>

        <Box sx={{ flex: 1 }} />

        {/* A dealer-owned car is titled to Skyway even when the buyer is local. */}
        {isFlorida && (
          <Button size="small" disabled={!ready || !!busy} onClick={() => generate('title-skyway')}>
            {busy === 'title-skyway' ? 'Making…' : 'Make in Skyway name instead'}
          </Button>
        )}

        <Button onClick={onClose} disabled={!!busy}>Close</Button>

        <Button
          variant="contained"
          disabled={!ready || !!busy}
          startIcon={busy === primaryDoctype ? <CircularProgress size={14} color="inherit" /> : <NoteAddIcon sx={{ fontSize: 16 }} />}
          onClick={() => generate(primaryDoctype)}
          sx={{ bgcolor: BLUE, '&:hover': { bgcolor: alpha(BLUE, 0.85) } }}
        >
          {busy === primaryDoctype ? 'Making…' : `Make for ${titledTo}`}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
